/**
 * Cloud Operations — shared UI primitives.
 *
 * Centralises the V2 palette + form primitives + slideout chrome so every
 * page in the section renders consistently.
 *
 * Per memory feedback_v2_inline_over_modal — anchored slideouts only.
 * Per memory feedback_v2_form_primitives_mandatory — Field/Input/Select wrappers.
 */

import React from 'react';

export const T = {
  ink:        '#0a1d44',
  ink2:       '#0e3552',
  text:       '#1a1f2b',
  text2:      '#5b6b7e',
  text3:      '#8896a8',
  stroke:     '#d4dbe5',
  stroke2:    '#e8edf3',
  bgCard:     '#ffffff',
  bgTint:     '#f7f9fc',
  bgAlt:      '#eef2f8',
  success:    '#2e7d4f',
  successBg:  '#e7f4ec',
  warn:       '#c58b00',
  warnBg:     '#fff4d9',
  danger:     '#c0392b',
  dangerBg:   '#fbe9e7',
  info:       '#1f5fa6',
  infoBg:     '#e8f0fb',
  action:     '#0e3552',
  actionHover:'#18496c',
};

export const inp = {
  fontFamily: 'inherit', fontSize: 13, padding: '7px 10px',
  border: `1px solid ${T.stroke}`, borderRadius: 4, background: '#fff', color: T.text,
  width: '100%', boxSizing: 'border-box',
};
export const inpReadOnly = { ...inp, background: T.bgAlt, color: T.text2 };

export const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 4, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: `1px solid ${T.stroke}`, background: '#fff', color: T.ink,
};
export const btnPrimary  = { ...btn, background: T.action, border: `1px solid ${T.action}`, color: '#fff' };
export const btnSm       = { ...btn, padding: '4px 10px', fontSize: 12 };
export const btnSmGhost  = { ...btnSm, border: '1px solid transparent', background: 'transparent', color: T.text2 };
export const btnSmDanger = { ...btnSm, color: T.danger, border: `1px solid ${T.danger}` };

export function Field({ label, hint, required, readOnly, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
        {label}{required && <span style={{ color: T.danger, marginLeft: 2 }}>*</span>}
        {readOnly && <span style={{ color: T.text3, marginLeft: 8, fontStyle: 'italic' }}>(read-only)</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: T.text2 }}>{hint}</div>}
    </div>
  );
}

export function Banner({ color, bg, children }) {
  return <div style={{
    background: bg, borderLeft: `3px solid ${color}`, padding: '10px 14px',
    borderRadius: 4, color, fontSize: 13, margin: '12px 0',
  }}>{children}</div>;
}

export function Th({ children }) {
  return <th style={{
    textAlign: 'left', padding: '10px 12px', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: '.06em',
    color: T.text2, fontWeight: 600, borderBottom: `1px solid ${T.stroke}`,
  }}>{children}</th>;
}
export function Td({ children, style }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top', ...(style || {}) }}>{children}</td>;
}

export function Slideout({ open, onClose, width = 560, children }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(10,29,68,.18)', zIndex: 30,
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width, maxWidth: '92vw',
        background: T.bgCard, borderLeft: `1px solid ${T.stroke}`, zIndex: 31,
        boxShadow: '-12px 0 32px rgba(10,29,68,.12)',
        display: 'flex', flexDirection: 'column',
      }}>{children}</div>
    </>
  );
}

export function ProviderPill({ provider }) {
  const colors = {
    gcp:   { fg: '#1f5fa6', bg: '#ecf3f8', mark: 'G' },
    aws:   { fg: '#7a4d00', bg: '#fff4d9', mark: 'A' },
    azure: { fg: '#1f5fa6', bg: '#ecf3f8', mark: 'Z' },
  };
  const c = colors[provider] || { fg: T.text2, bg: T.bgAlt, mark: '?' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontWeight: 600, fontSize: 12, padding: '2px 8px 2px 4px',
      borderRadius: 4, border: `1px solid ${c.fg}33`,
      background: c.bg, color: c.fg,
    }}>
      <span style={{
        width: 16, height: 16, display: 'grid', placeItems: 'center',
        background: c.fg, color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 800,
      }}>{c.mark}</span>
      {(provider || '—').toUpperCase()}
    </span>
  );
}

/**
 * Display label for a deployment mode.
 *
 * Backend enum stays `prove7_hosted` for column/wire stability
 * (see memory: reference_prove7_cloud_naming). Display flips to
 * "Prove7 Cloud" everywhere.
 */
export function modeLabel(mode) {
  if (mode === 'prove7_hosted') return 'Prove7 Cloud';
  if (mode === 'self_hosted')   return 'Self-Hosted';
  return mode || '—';
}

export function ModePill({ mode }) {
  const isCloud = mode === 'prove7_hosted';
  const fg = isCloud ? '#1f5fa6' : '#6b3f9e';
  const bg = isCloud ? '#ecf3f8' : '#f3edf8';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: fg, background: bg,
      border: `1px solid ${fg}33`,
    }}>{modeLabel(mode)}</span>
  );
}

export function StatusPill({ status, scheme = 'default' }) {
  const schemes = {
    default: {
      pending:        { fg: T.warn,    bg: T.warnBg,    label: 'Pending' },
      active:         { fg: T.success, bg: T.successBg, label: 'Active' },
      revoked:        { fg: T.danger,  bg: T.dangerBg,  label: 'Revoked' },
      error:          { fg: T.danger,  bg: T.dangerBg,  label: 'Error' },
      retired:        { fg: T.text3,   bg: T.bgAlt,     label: 'Retired' },
      quarantined:    { fg: T.warn,    bg: T.warnBg,    label: 'Quarantined' },
      provisioning:   { fg: T.info,    bg: T.infoBg,    label: 'Provisioning' },
      healthy:        { fg: T.success, bg: T.successBg, label: 'Healthy' },
      drifted:        { fg: T.warn,    bg: T.warnBg,    label: 'Drifted' },
      degraded:       { fg: T.warn,    bg: T.warnBg,    label: 'Degraded' },
      failed:         { fg: T.danger,  bg: T.dangerBg,  label: 'Failed' },
      upgrading:      { fg: T.info,    bg: T.infoBg,    label: 'Upgrading' },
      decommissioning:{ fg: T.text3,   bg: T.bgAlt,     label: 'Decommissioning' },
      decommissioned: { fg: T.text3,   bg: T.bgAlt,     label: 'Decommissioned' },
      draft:          { fg: T.warn,    bg: T.warnBg,    label: 'Draft' },
      deprecated:     { fg: T.text3,   bg: T.bgAlt,     label: 'Deprecated' },
    },
    run: {
      in_progress: { fg: T.info,    bg: T.infoBg,    label: 'In progress' },
      success:     { fg: T.success, bg: T.successBg, label: 'Success'     },
      failed:      { fg: T.danger,  bg: T.dangerBg,  label: 'Failed'      },
      cancelled:   { fg: T.text2,   bg: T.bgAlt,     label: 'Cancelled'   },
      timed_out:   { fg: T.warn,    bg: T.warnBg,    label: 'Timed out'   },
    },
  };
  const c = schemes[scheme]?.[status] || { fg: T.text2, bg: T.bgAlt, label: status || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{c.label}</span>
  );
}

export function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length <= 12 ? s : s.slice(0, 8) + '…' + s.slice(-4);
}

export function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}
